import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Users() {
    const { user, hotels } = useAuth();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", password: "", name: "", role: "front_desk", hotel_ids: [] });

    const load = async () => {
        try {
            const [u, r] = await Promise.all([api.get("/auth/users"), api.get("/auth/roles")]);
            setUsers(u.data); setRoles(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        try {
            await api.post("/auth/register", form);
            toast.success("User created");
            setOpen(false); setForm({ email: "", password: "", name: "", role: "front_desk", hotel_ids: [] });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this user?")) return;
        try { await api.delete(`/auth/users/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const canDelete = ["super_admin", "hotel_owner"].includes(user?.role);

    return (
        <AppShell title="Users & Roles" subtitle={`${users.length} users · ${roles.length} roles`}>
            <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="user-add-btn"><Plus className="w-4 h-4 mr-1" /> User</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Full name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Password</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Role</Label>
                                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>{roles.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Assign Hotel</Label>
                                <Select value={form.hotel_ids[0] || ""} onValueChange={v => setForm({ ...form, hotel_ids: v ? [v] : [] })}>
                                    <SelectTrigger><SelectValue placeholder="Select a hotel" /></SelectTrigger>
                                    <SelectContent>{hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                                </Select></div>
                        </div>
                        <DialogFooter><Button onClick={submit} disabled={!form.email || !form.password || !form.name} className="bg-[#0A1C3E]" data-testid="user-submit">Create</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Hotels</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        {u.role === "super_admin" && <Shield className="w-3.5 h-3.5 text-[#C5B08A]" />}
                                        <span className="text-sm font-medium">{u.name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600 font-mono">{u.email}</td>
                                <td className="px-4 py-3">
                                    <span className="status-badge bg-slate-100 text-slate-700 border-slate-200">{u.role?.replace(/_/g, " ")}</span>
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">{u.hotel_ids?.length || 0} assigned</td>
                                <td className="px-4 py-3 text-right">
                                    {canDelete && u.role !== "super_admin" && (
                                        <button onClick={() => remove(u.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`user-del-${u.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
